import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ReactElement } from "react";
import { PromiseStatuses } from "../../../../lib/utilis/index.ts";
import { BrightyMessagesResponseDTO, BrightyFilters, BrightyMessage, UserMessage, FindBrightyChatByIdDTO, TitleResult, getAllTitlesResponseDTO } from "./dto.ts";
import { NewBrightyService } from "./service.ts";

interface BrightyState {
    findBrightyChatsStatus: PromiseStatuses
    sendMessageStatus: PromiseStatuses
    findBrightyChatResponse?: BrightyMessagesResponseDTO
    findBrightyChatByIdResponse?: FindBrightyChatByIdDTO
    findBrightyChatByIdStatus: PromiseStatuses
    getTitleResponse?: string
    getAllTitlesResponse?: getAllTitlesResponseDTO
    getAllTitlesStatus: PromiseStatuses
    getTitleStatus: PromiseStatuses
    deleteChatStatus: PromiseStatuses
    filters: BrightyFilters
    selectedChat: number
    message: UserMessage
    precision: number
    selectedChatMessages: {
        id: string
        data: BrightyMessage[]
    }[]
    textMessage: string
    progress: number
    stage: string
    added: boolean
    written: boolean
    openConfirmModal: boolean
    idToDelete: number
    menuClosed: boolean
    fromHome: boolean
}

const initialState: BrightyState = {
    findBrightyChatsStatus: 'idle',
    filters: {
        page: 0,
        page_size: 0,
        thread_id: undefined,
        user: undefined
    },
    precision: 0.5,
    selectedChat: 0,
    selectedChatMessages: [],
    message: {
        precision: 0.5,
        question: '',
        thread_id: ""
    },
    sendMessageStatus: "idle",
    findBrightyChatByIdStatus: "idle",
    progress: 0,
    getTitleStatus: "idle",
    written: false,
    getAllTitlesStatus: "idle",
    added: false,
    textMessage: "",
    deleteChatStatus: "idle",
    openConfirmModal: false,
    idToDelete: 0,
    stage: "Attesa",
    menuClosed: false,
    fromHome: false
}

export const findBrightyChats = createAsyncThunk(
    'brighty/findChats',
    async (filters: BrightyFilters): Promise<BrightyMessagesResponseDTO> => {
        const brightyService = NewBrightyService()

        return brightyService.findBrightyChats(filters)
    },
)

export const findBrightyChatById = createAsyncThunk(
    'brighty/findChatById',
    async (request: { id: string }): Promise<FindBrightyChatByIdDTO> => {
        const brightyService = NewBrightyService()

        return brightyService.findBrightyChatById(request.id)
    },
)

export const sendMessage = createAsyncThunk(
    'brighty/sendMessage',
    async (message: UserMessage): Promise<void> => {
        const brightyService = NewBrightyService()

        return brightyService.sendMessage(message)
    },
)


export const getTitle = createAsyncThunk(
    'brighty/getTitle',
    async (thread_id: string): Promise<string> => {
        const brightyService = NewBrightyService()

        return brightyService.getTitle(thread_id) as unknown as string
    },
)

export const deleteChat = createAsyncThunk(
    'brighty/deleteChat',
    async (thread_id: string): Promise<void> => {
        const brightyService = NewBrightyService()

        return brightyService.deleteChat(thread_id)
    },
)

export const getAllTitles = createAsyncThunk(
    'brighty/getAllTitles',
    async (): Promise<getAllTitlesResponseDTO> => {
        const brightyService = NewBrightyService();

        return (brightyService.getTitle() as unknown as getAllTitlesResponseDTO)
    }
)

const brightySlice = createSlice({
    name: 'brighty/slice',
    initialState,
    reducers: {
        setAdded: (state, action) => {
            state.added = action.payload
        },
        setFiltersUser: (state, action) => {
            state.filters.user = action.payload
        },
        setFiltersThreadId: (state, action) => {
            state.filters.thread_id = action.payload
        },
        setSelectedChat: (state, action) => {
            state.selectedChat = action.payload
        },
        setSelectedMessages: (state, action) => {
            state.selectedChatMessages = action.payload
        },
        setFindBrightyChatByIdStatus: (state, action) => {
            state.findBrightyChatByIdStatus = action.payload
        },
        setGetTitleStatus: (state, action) => {
            state.getTitleStatus = action.payload
        },
        setPrecision: (state, action) => {
            state.precision = action.payload
        },
        setStateProgress: (state, action) => {
            state.progress = action.payload
        },
        setStateStage: (state, action) => {
            state.stage = action.payload
        },
        setTextMessage: (state, action) => {
            state.textMessage = action.payload
        },
        setWritten: (state, action) => {
            state.written = action.payload
        },
        setOpenConfirmModal: (state, action) => {
            state.openConfirmModal = action.payload
        },
        setBrightyIdToDelete: (state, action) => {
            state.idToDelete = action.payload
        },
        setMenuClosed: (state, action) => {
            state.menuClosed = action.payload
        },
        setFromHome: (state, action) => {
            state.fromHome = action.payload
        }
    },
    extraReducers(builder) {
        builder
            .addCase(findBrightyChats.pending, (state) => {
                state.findBrightyChatsStatus = 'loading'
            })
            .addCase(findBrightyChats.fulfilled, (state, action) => {
                state.findBrightyChatsStatus = 'successfully'
                state.findBrightyChatResponse = action.payload
            })
            .addCase(findBrightyChats.rejected, (state) => {
                state.findBrightyChatsStatus = 'failed'
            }).addCase(findBrightyChatById.pending, (state) => {
                state.findBrightyChatByIdStatus = 'loading'
            })
            .addCase(findBrightyChatById.fulfilled, (state, action) => {
                state.findBrightyChatByIdStatus = 'successfully'
                state.findBrightyChatByIdResponse = action.payload
            })
            .addCase(findBrightyChatById.rejected, (state) => {
                state.findBrightyChatByIdStatus = 'failed'
            })
            .addCase(sendMessage.pending, (state) => {
                state.sendMessageStatus = 'loading'
            })
            .addCase(sendMessage.fulfilled, (state, action) => {
                state.sendMessageStatus = 'successfully'
            })
            .addCase(sendMessage.rejected, (state) => {
                state.sendMessageStatus = 'failed'
            })
            .addCase(deleteChat.pending, (state) => {
                state.deleteChatStatus = 'loading'
            })
            .addCase(deleteChat.fulfilled, (state, action) => {
                state.deleteChatStatus = 'successfully'
            })
            .addCase(deleteChat.rejected, (state) => {
                state.deleteChatStatus = 'failed'
            })
            .addCase(getTitle.pending, (state) => {
                state.getTitleStatus = 'loading'
            })
            .addCase(getTitle.fulfilled, (state, action) => {
                state.getTitleStatus = 'successfully'
                state.getTitleResponse = action.payload
            })
            .addCase(getTitle.rejected, (state) => {
                state.getTitleStatus = 'failed'
            })
            .addCase(getAllTitles.pending, (state) => {
                state.getAllTitlesStatus = 'loading'
            })
            .addCase(getAllTitles.fulfilled, (state, action) => {
                state.getAllTitlesStatus = 'successfully'
                state.getAllTitlesResponse = action.payload
            })
            .addCase(getAllTitles.rejected, (state) => {
                state.getAllTitlesStatus = 'failed'
            })
    },
})

export const {
    setAdded, setBrightyIdToDelete, setMenuClosed, setFromHome,
    setFiltersThreadId, setFiltersUser, setSelectedChat, setSelectedMessages, setTextMessage, setFindBrightyChatByIdStatus, setGetTitleStatus, setOpenConfirmModal, setPrecision, setStateStage, setStateProgress, setWritten
} = brightySlice.actions

export default brightySlice.reducer
